import { onMounted, onUnmounted } from "vue";
import { useAuthStore } from "@/stores/auth";

/**
 * Activity-pohjainen automaattinen session-refresh.
 *
 * Aiempi käytös: access-token voimassa 15 min, eikä GUI refreshannut
 * lainkaan → token vanhentui ja seuraavalla pyynnöllä `authFetch`
 * heitti käyttäjän /login:iin. Operaattorit valittivat että
 * "kirjautuu kokoajan pois" vaikka olisivat olleet aktiivisia.
 *
 * Nyt token on voimassa 30 min ja tämä komposiitti pidentää sitä
 * automaattisesti aktiivisille käyttäjille:
 *
 *   - Selaimen interaktioista (mousemove, keydown, click, scroll)
 *     päivitetään `lastActivityAt` (throttlattu — yksi merkintä /
 *     ACTIVITY_THROTTLE_MS, ettei joka mousemove tappa CPU:ta).
 *   - Joka tarkistus-intervallin (CHECK_INTERVAL_MS) verran
 *     katsotaan:
 *       * Onko access-tokenia jäljellä vähemmän kuin
 *         REFRESH_THRESHOLD_MS? (5 min jäljellä → refreshataan)
 *       * Oliko käyttäjä aktiivinen viime IDLE_TIMEOUT_MS:n sisällä?
 *         Jos ei → annetaan tokenin vanheta luonnostaan,
 *         istunto päättyy.
 *   - Onnistunut refresh kutsuu silent-API:n joka palauttaa uuden
 *     access+refresh-parin → `accessTokenExpiresAt` rullaa eteenpäin
 *     → seuraava interaktio jatkuu seamlessina.
 *
 * Aktivoidaan App.vue:n setup():ssa. Käynnistää itse event-listenerit
 * + intervalin mountatessa, siivoaa unmountilla.
 */

/// Throttle window aktivity-tagien päivitykselle. Mousemove voi
/// laueta satoja kertoja sekunnissa — 5 sekunnin granulariteetti
/// riittää kun "viime aktiviteetti"-päätökset tehdään minuuttitasolla.
const ACTIVITY_THROTTLE_MS = 5_000;

/// Kuinka usein säikeessä tarkistetaan refresh-tarve. 60 sekuntia
/// on tasapaino "ei näy CPU-pollausarvoissa" ja "ei jää myöhässä
/// kun kynnys ylittyy" välillä.
const CHECK_INTERVAL_MS = 60_000;

/// Jos accessTokenExpiresAt - now < tämä → refreshataan. 5 min antaa
/// reilun varan kun token TTL on 30 min — verkko-blipit eivät ehdi
/// tappamaan sessiota ennen kuin seuraava check-intervalli ehtii.
const REFRESH_THRESHOLD_MS = 5 * 60_000;

/// Idle-raja: jos käyttäjä ei ole tehnyt mitään tämän ajan sisällä,
/// EI refreshata. Toimettomat selaimet (esim. yön yli auki jätetty
/// välilehti) vanhenevat luonnostaan. Pidetään yhtä pitkänä kuin
/// access-tokenin TTL (30 min) — käyttäjä jolla on selain auki mutta
/// ei tee mitään tunnin ajan menettää session.
const IDLE_TIMEOUT_MS = 30 * 60_000;

/// DOM-tapahtumat joiden katsotaan olevan "käyttäjän aktiviteettia".
/// `passive: true` jotta scroll-listeneri ei sotke selaimen optimointia.
const ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "click",
  "scroll",
  "touchstart",
] as const;

export function useSessionRefresh(): void {
  const auth = useAuthStore();
  let lastMarked = 0;

  function onActivity(): void {
    const now = Date.now();
    if (now - lastMarked < ACTIVITY_THROTTLE_MS) return;
    lastMarked = now;
    auth.markActivity();
  }

  async function maybeRefresh(): Promise<void> {
    if (!auth.isAuthenticated) return;
    const expiresAt = auth.accessTokenExpiresAt;
    if (typeof expiresAt !== "number") return; // tuntematon (esim. juuri ladattu localStoragesta)
    const now = Date.now();
    const msUntilExpiry = expiresAt - now;
    if (msUntilExpiry > REFRESH_THRESHOLD_MS) return; // ei vielä kynnyksessä
    const msSinceActivity = now - auth.lastActivityAt;
    if (msSinceActivity > IDLE_TIMEOUT_MS) return; // toimeton — anna vanheta
    await auth.refresh();
  }

  let intervalHandle: ReturnType<typeof setInterval> | null = null;

  onMounted(() => {
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    // Aja heti mountilla — restartin jälkeen accessTokenExpiresAt
    // voi olla null (= tuntematon), jolloin maybeRefresh palaa
    // saman tien. Käynnistys vain initialisoi intervalin.
    intervalHandle = setInterval(() => void maybeRefresh(), CHECK_INTERVAL_MS);
  });

  onUnmounted(() => {
    for (const ev of ACTIVITY_EVENTS) {
      window.removeEventListener(ev, onActivity);
    }
    if (intervalHandle) clearInterval(intervalHandle);
  });
}
